import {
  getProductPurchaseKey,
  getProductsPurchaseKey,
  ProductPurchaseLiteModel,
  ProductPurchaseModel,
  useGetProductPurchase,
  useGetProductsPurchase,
} from 'api-hooks/product/order';
import { Option } from 'baseui/select';
import { PurchaseStatusEnum } from 'common/constants/enum';
import { QueryTransformer } from 'common/helpers/common';
import { transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

function transformer(
  item: ProductPurchaseLiteModel | ProductPurchaseModel,
): Option {
  const agentName =
    typeof item.agent === 'object' ? item.agent.name : item.agent;

  return {
    id: item.id,
    label: transformLabel([item.number, agentName]),
    extra: item,
  };
}

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value?: any) => void;
  onAfterDetailChange?: (value: ProductPurchaseModel) => void;
  noMargin?: boolean;
  disabled?: boolean;
}

export default function ProductPurchaseSelectInput(props: Props) {
  const { t } = useTranslation();
  const { label = t('common:order'), placeholder = t('common:select_order') } =
    props;
  const { control, setValue } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });

  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value[0]?.id || '', {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    useListQueryHook: useGetProductsPurchase,
    useDetailLazyQueryHook: useGetProductPurchase,
    listKeyFunction: getProductsPurchaseKey,
    detailKeyFunction: getProductPurchaseKey,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
          filter: { status: PurchaseStatusEnum.Pending },
        },
      }),
      [],
    ),
    renderCreate: false,
    onClickCreate: React.useCallback(() => {
      props.onClose?.();
    }, [props]),
    getMemoizedDetailVariables: React.useCallback(
      (value: string) => ({
        id: value,
      }),
      [],
    ),
    listTransformer: React.useCallback((result) => {
      return result.data.map((v) => transformer(v));
    }, []),
    detailTransformer: React.useCallback(
      (result) => {
        const newResult = QueryTransformer(result, ProductPurchaseModel);
        props.onAfterDetailChange?.(newResult.data);
        return transformer(newResult.data);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      overrides={overrides}
      disabled={props.disabled}
      {...selectProps}
    />
  );
}
